import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getMe, listApps, getToken, changePassword } from "../api";

function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.new_password.length < 8) return setError("New password must be at least 8 characters");
    if (form.new_password !== form.confirm_password) return setError("New passwords do not match");
    setLoading(true);
    setError("");
    try {
      await changePassword(form.current_password, form.new_password);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={modal.overlay}>
      <div style={modal.box}>
        <h2 style={modal.title}>Change Password</h2>
        {error && <div style={modal.error}>{error}</div>}
        {done ? (
          <>
            <p style={modal.successText}>✓ Password updated successfully.</p>
            <div style={modal.actions}>
              <button type="button" onClick={onClose} style={modal.createBtn}>Done</button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} style={modal.form}>
            <label style={modal.label}>Current Password</label>
            <input name="current_password" type="password" value={form.current_password} onChange={handleChange} style={modal.input} required autoComplete="current-password" />

            <label style={modal.label}>New Password</label>
            <input name="new_password" type="password" value={form.new_password} onChange={handleChange} style={modal.input} required minLength={8} autoComplete="new-password" />

            <label style={modal.label}>Confirm New Password</label>
            <input name="confirm_password" type="password" value={form.confirm_password} onChange={handleChange} style={modal.input} required autoComplete="new-password" />

            <div style={modal.actions}>
              <button type="button" onClick={onClose} style={modal.cancelBtn}>Cancel</button>
              <button type="submit" disabled={loading} style={modal.createBtn}>
                {loading ? "Saving..." : "Save Password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    Promise.all([getMe(), listApps()])
      .then(([u, a]) => { setUser(u); setApps(a); })
      .catch(() => { localStorage.removeItem("token"); navigate("/login"); });
  }, [navigate]);

  function handleLogout() {
    localStorage.removeItem("token");
    navigate("/login");
  }

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.logo}>AutoRev</span>
        <div style={s.navRight}>
          {user && <span style={s.email}>{user.email}</span>}
          <button onClick={() => setShowChangePassword(true)} style={s.changePasswordBtn}>Change Password</button>
          <button onClick={handleLogout} style={s.logoutBtn}>Logout</button>
          {/* Only an admin sees this, and the admin area re-checks the claim on
              entry — this link is a convenience, never the access control. */}
          {user?.is_admin && (
            <button onClick={() => navigate("/admin")} style={s.adminBtn}>Admin Dashboard</button>
          )}
        </div>
      </nav>

      <main style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.h1}>Your Apps</h1>
            <p style={s.sub}>Each app has its own PhonePe credentials and integration URLs.</p>
          </div>
        </div>

        {apps.length === 0 ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>📦</div>
            <p style={s.emptyText}>No apps yet. Contact an admin to have one created for you.</p>
          </div>
        ) : (
          <div style={s.grid}>
            {apps.map((app) => (
              <div key={app.id} style={s.card}>
                <div style={s.cardTop}>
                  <div style={s.appIcon}>{app.name[0].toUpperCase()}</div>
                  <div>
                    <div style={s.appName}>{app.name}</div>
                    <div style={s.appSlug}>{app.id}</div>
                  </div>
                </div>
                <div style={s.cardStatus}>
                  {app.phonepe_enabled && (
                    <div>
                      <span style={{ ...s.envBadge, ...(app.phonepe_environment === "PROD" ? s.prodBadge : s.uatBadge) }}>
                        PhonePe · {app.phonepe_environment}
                      </span>{" "}
                      <span style={app.phonepe_client_id ? s.connected : s.notConnected}>
                        {app.phonepe_client_id ? "✓ connected" : "⚠ credentials not set"}
                      </span>
                    </div>
                  )}
                  {app.razorpay_enabled && (
                    <div>
                      <span style={{ ...s.envBadge, ...(app.razorpay_environment === "LIVE" ? s.prodBadge : s.uatBadge) }}>
                        Razorpay · {app.razorpay_environment}
                      </span>{" "}
                      <span style={app.razorpay_key_id ? s.connected : s.notConnected}>
                        {app.razorpay_key_id ? "✓ connected" : "⚠ credentials not set"}
                      </span>
                    </div>
                  )}
                </div>
                <div style={s.cardActions}>
                  {/* A real <a href> rather than a button, so ctrl/cmd-click,
                      middle-click and "Open link in new tab" all work natively;
                      plain clicks still route in-app without a reload. */}
                  <Link to={`/apps/${app.id}`} style={s.openBtn}>Open Dashboard</Link>
                  <a
                    href={`/apps/${app.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={s.newTabBtn}
                    title="Open in a new tab"
                    aria-label={`Open ${app.name} in a new tab`}
                  >
                    ↗
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", backgroundColor: "#f8fafc", fontFamily: "system-ui, sans-serif" },
  nav: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 32px", backgroundColor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  logo: { fontWeight: 700, fontSize: 20, color: "#4f46e5" },
  navRight: { display: "flex", alignItems: "center", gap: 16 },
  adminBtn: { padding: "6px 14px", backgroundColor: "#1e293b", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 500, whiteSpace: "nowrap" },
  email: { color: "#64748b", fontSize: 14 },
  changePasswordBtn: { padding: "6px 14px", backgroundColor: "#f1f5f9", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", fontSize: 14 },
  logoutBtn: { padding: "6px 14px", backgroundColor: "#ef4444", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14 },
  main: { padding: "40px 32px", maxWidth: 1000, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 },
  h1: { margin: "0 0 4px", fontSize: 26, color: "#1e293b" },
  sub: { margin: 0, color: "#64748b", fontSize: 14 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 },
  card: { backgroundColor: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", display: "flex", flexDirection: "column", gap: 14 },
  cardTop: { display: "flex", alignItems: "center", gap: 12 },
  appIcon: { width: 40, height: 40, borderRadius: 10, backgroundColor: "#ede9fe", color: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 18, flexShrink: 0 },
  appName: { fontWeight: 600, color: "#1e293b", fontSize: 15 },
  appSlug: { color: "#94a3b8", fontSize: 12, marginTop: 2 },
  envBadge: { marginLeft: "auto", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 },
  uatBadge: { backgroundColor: "#fef9c3", color: "#854d0e" },
  prodBadge: { backgroundColor: "#dcfce7", color: "#166534" },
  cardStatus: { fontSize: 13, display: "flex", flexDirection: "column", gap: 6 },
  connected: { color: "#16a34a" },
  notConnected: { color: "#d97706" },
  cardActions: { display: "flex", gap: 8, marginTop: 4 },
  openBtn: { flex: 1, padding: "8px 0", backgroundColor: "#4f46e5", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, textAlign: "center", textDecoration: "none", display: "block" },
  newTabBtn: { flexShrink: 0, width: 34, padding: "8px 0", backgroundColor: "#fff", color: "#4f46e5", border: "1px solid #ddd6fe", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, textAlign: "center", textDecoration: "none", display: "block" },
  empty: { textAlign: "center", padding: "80px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 },
  emptyIcon: { fontSize: 48 },
  emptyText: { color: "#64748b", margin: 0 },
};

const modal = {
  overlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  box: { backgroundColor: "#fff", borderRadius: 12, padding: 32, width: "100%", maxWidth: 400, maxHeight: "90vh", overflowY: "auto" },
  title: { margin: "0 0 20px", fontSize: 20, color: "#1e293b" },
  form: { display: "flex", flexDirection: "column", gap: 10 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151" },
  input: { padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14, width: "100%", boxSizing: "border-box" },
  error: { padding: 10, backgroundColor: "#fee2e2", color: "#dc2626", borderRadius: 6, fontSize: 14, marginBottom: 4 },
  successText: { padding: 10, backgroundColor: "#dcfce7", color: "#166534", borderRadius: 6, fontSize: 14 },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 },
  cancelBtn: { padding: "9px 18px", backgroundColor: "#f1f5f9", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14 },
  createBtn: { padding: "9px 18px", backgroundColor: "#4f46e5", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 },
};
